import { AXIOS_INSTANCE } from '../services/api-client';

export interface RuntimeProviderAuthRequired {
  source_type?: string | null;
  provider?: string | null;
  connector_config_id?: string | number | null;
  workspace_id?: string | number | null;
  reason?: string | null;
  code?: string | null;
  message?: string | null;
  authorize_url?: string | null;
  pending_request_id?: string | null;
  run_id?: string | null;
  conversation_id?: string | null;
}

export interface RuntimeProviderAuthResumeResponse {
  run_id?: string | null;
  conversation_id?: string | null;
  status?: string | null;
}

export function normalizeProviderKey(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

export function getRuntimeProviderLabel(auth: RuntimeProviderAuthRequired): string {
  const provider = normalizeProviderKey(auth.provider);
  const sourceType = normalizeProviderKey(auth.source_type);

  if (provider === 'google' || sourceType === 'bigquery' || sourceType === 'google_bigquery') {
    return 'Google';
  }

  const raw = provider || sourceType || 'provider';
  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getRuntimeSourceLabel(auth: RuntimeProviderAuthRequired): string {
  const sourceType = normalizeProviderKey(auth.source_type);
  if (sourceType === 'bigquery' || sourceType === 'google_bigquery') return 'BigQuery';
  if (!sourceType) return getRuntimeProviderLabel(auth);
  return sourceType
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function providerAuthMatchesOAuthMessage(
  auth: RuntimeProviderAuthRequired,
  data: any,
): boolean {
  if (!data || data.type !== 'oauth_complete') return false;

  const expected = new Set(
    [
      auth.source_type,
      auth.provider,
      normalizeProviderKey(auth.source_type) === 'bigquery' ? 'google' : null,
    ]
      .map((value) => normalizeProviderKey(value))
      .filter(Boolean),
  );

  const actual = normalizeProviderKey(data.source || data.provider);
  if (expected.size > 0 && !expected.has(actual)) return false;

  const expectedRunId = auth.run_id || null;
  const actualRunId = data.run_id || null;
  if (expectedRunId && actualRunId && String(expectedRunId) !== String(actualRunId)) return false;

  const expectedPendingRequestId = auth.pending_request_id || null;
  const actualPendingRequestId = data.pending_request_id || null;
  if (
    expectedPendingRequestId &&
    actualPendingRequestId &&
    String(expectedPendingRequestId) !== String(actualPendingRequestId)
  ) {
    return false;
  }

  const expectedConversationId = auth.conversation_id || null;
  const actualConversationId = data.conversation_id || null;
  if (
    expectedConversationId &&
    actualConversationId &&
    String(expectedConversationId) !== String(actualConversationId)
  ) {
    return false;
  }

  return true;
}

export function extractProviderAuthRequired(
  runResponse: any,
  fallbackRunId?: string | null,
): RuntimeProviderAuthRequired | null {
  const candidates = [
    runResponse?.provider_auth_required,
    runResponse?.auth_required,
    runResponse?.metadata?.provider_auth_required,
    runResponse?.metadata?.auth_required,
    runResponse?.results?.provider_auth_required,
    runResponse?.results?.auth_required,
  ];

  const auth = candidates.find((candidate) => candidate && typeof candidate === 'object');
  if (!auth) return null;

  const code = normalizeProviderKey(auth.code || runResponse?.status);
  const status = normalizeProviderKey(runResponse?.status);
  const isAuthRequired =
    status === 'auth_required' ||
    status === 'provider_auth_required' ||
    code === 'auth_required' ||
    code === 'auth_expired' ||
    Boolean(auth.authorize_url);

  if (!isAuthRequired) return null;

  return {
    source_type: auth.source_type || auth.source || auth.connector_type || null,
    provider: auth.provider || null,
    connector_config_id: auth.connector_config_id || auth.connection_id || null,
    workspace_id: auth.workspace_id || runResponse?.workspace_id || null,
    reason: auth.reason || null,
    code: auth.code || runResponse?.status || null,
    message: auth.message || runResponse?.message || null,
    authorize_url: auth.authorize_url || auth.authorization_url || null,
    pending_request_id: auth.pending_request_id || runResponse?.pending_request_id || null,
    run_id: auth.run_id || runResponse?.run_id || fallbackRunId || null,
    conversation_id: auth.conversation_id || runResponse?.conversation_id || null,
  };
}

export function stripAuthMetadataFromResults(results: any): any {
  if (!results || typeof results !== 'object' || Array.isArray(results)) {
    return results;
  }

  const { auth_required, provider_auth_required, ...rest } = results;
  const keys = Object.keys(rest);
  return keys.length > 0 ? rest : null;
}

export async function resumeRetrievalRun(
  runId: string,
  auth: RuntimeProviderAuthRequired,
): Promise<RuntimeProviderAuthResumeResponse> {
  const { data } = await AXIOS_INSTANCE.post(`/api/retrieval/runs/${runId}/resume`, {
    pending_request_id: auth.pending_request_id || undefined,
    source_type: auth.source_type || undefined,
    provider: auth.provider || undefined,
    connector_config_id: auth.connector_config_id || undefined,
    workspace_id: auth.workspace_id || undefined,
    conversation_id: auth.conversation_id || undefined,
  });
  return data || {};
}
